import React, { useEffect, useState } from 'react';
import {
    Alert, Box, Button, CircularProgress, MenuItem, Paper, Table, TableBody, TableCell,
    TableContainer, TableFooter, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { StatutoryRegisterService } from '../../services/statutoryRegister.service';
import { IWageSheetDTO, IWageSheetRow, WageSheetFormat } from '../../interfaces/statutoryRegister.interface';
import { useToast } from '../../context/ToastContext';
import { dash, money, MONTH_NAMES, sanitizeFilenameSegment, triggerDownload, WAGE_SHEET_COLUMN_META, WAGE_SHEET_FORMATS } from './shared';
import { helpElement } from '../../help/attrs';
import OrgUnitPicker from '../common/OrgUnitPicker';

interface Props {
    institutionId: string;
}

// Default to LAST month: the current month's payroll usually isn't run yet, so opening on it
// shows an empty sheet (same rule the payroll list uses).
const lastMonthDate = () => { const n = new Date(); return new Date(n.getFullYear(), n.getMonth() - 1, 1); };
const currentYear = () => lastMonthDate().getFullYear();
const currentMonth = () => lastMonthDate().getMonth() + 1;

/** Help key per wage sheet column (the sheet's columns depend on the chosen format, so the keys live in this literal map). */
const COLUMN_HELP: Record<string, { helpKey: string }> = {
    EMPLOYEE_CODE: { helpKey: 'statutory.wageSheet.col.employeeCode' },
    EMPLOYEE_NAME: { helpKey: 'statutory.wageSheet.col.employeeName' },
    DESIGNATION: { helpKey: 'statutory.wageSheet.col.designation' },
    DEPARTMENT: { helpKey: 'statutory.wageSheet.col.department' },
    DATE_OF_JOINING: { helpKey: 'statutory.wageSheet.col.dateOfJoining' },
    WAGE_GRADE: { helpKey: 'statutory.wageSheet.col.wageGrade' },
    WAGE_MODE: { helpKey: 'statutory.wageSheet.col.wageMode' },
    DAYS_IN_MONTH: { helpKey: 'statutory.wageSheet.col.daysInMonth' },
    PAID_DAYS: { helpKey: 'statutory.wageSheet.col.paidDays' },
    ABSENT_DAYS: { helpKey: 'statutory.wageSheet.col.absentDays' },
    UNPAID_LEAVE_DAYS: { helpKey: 'statutory.wageSheet.col.unpaidLeaveDays' },
    BASIC: { helpKey: 'statutory.wageSheet.col.basic' },
    HOUSE_RENT: { helpKey: 'statutory.wageSheet.col.houseRent' },
    MEDICAL: { helpKey: 'statutory.wageSheet.col.medical' },
    CONVEYANCE: { helpKey: 'statutory.wageSheet.col.conveyance' },
    FOOD: { helpKey: 'statutory.wageSheet.col.food' },
    OTHER_EARNINGS: { helpKey: 'statutory.wageSheet.col.otherEarnings' },
    GROSS_WAGE: { helpKey: 'statutory.wageSheet.col.grossWage' },
    OT_HOURS: { helpKey: 'statutory.wageSheet.col.otHours' },
    STATUTORY_OT_RATE: { helpKey: 'statutory.wageSheet.col.statutoryOtRate' },
    EFFECTIVE_OT_RATE: { helpKey: 'statutory.wageSheet.col.effectiveOtRate' },
    OT_AMOUNT: { helpKey: 'statutory.wageSheet.col.otAmount' },
    OT_RATE_SHORTFALL: { helpKey: 'statutory.wageSheet.col.otRateShortfall' },
    ATTENDANCE_BONUS: { helpKey: 'statutory.wageSheet.col.attendanceBonus' },
    FESTIVAL_BONUS: { helpKey: 'statutory.wageSheet.col.festivalBonus' },
    PIECE_RATE_AMOUNT: { helpKey: 'statutory.wageSheet.col.pieceRateAmount' },
    LEAVE_ENCASHMENT: { helpKey: 'statutory.wageSheet.col.leaveEncashment' },
    ABSENCE_DEDUCTION: { helpKey: 'statutory.wageSheet.col.absenceDeduction' },
    UNPAID_LEAVE_DEDUCTION: { helpKey: 'statutory.wageSheet.col.unpaidLeaveDeduction' },
    OTHER_DEDUCTIONS: { helpKey: 'statutory.wageSheet.col.otherDeductions' },
    TOTAL_DEDUCTIONS: { helpKey: 'statutory.wageSheet.col.totalDeductions' },
    NET_WAGE: { helpKey: 'statutory.wageSheet.col.netWage' },
    PAYROLL_STATUS: { helpKey: 'statutory.wageSheet.col.payrollStatus' },
    SIGNATURE: { helpKey: 'statutory.wageSheet.col.signature' },
    FLOOR: { helpKey: 'statutory.wageSheet.col.floor' },
    SECTION: { helpKey: 'statutory.wageSheet.col.section' },
    LINE: { helpKey: 'statutory.wageSheet.col.line' },
};

/**
 * Ninth statutory register tab: the audit-format wage sheet (decision note
 * docs/decisions/2026-09-11-garments-audit-wage-sheet-and-audit-pack.md §3(A)). Unlike the other
 * tabs' "select then click Load" pattern, this one refetches automatically on year/month/format
 * change -- switching the audit-standard preset is the whole point of the tab, and requiring an
 * extra click per format just to preview it would defeat that.
 */
const WageSheetTab: React.FC<Props> = ({ institutionId }) => {
    const toast = useToast();
    const [year, setYear] = useState(currentYear());
    const [month, setMonth] = useState(currentMonth());
    const [format, setFormat] = useState<WageSheetFormat>('FULL');
    const [orgUnitId, setOrgUnitId] = useState<string | null>(null);
    const [dto, setDto] = useState<IWageSheetDTO | null>(null);
    const [loading, setLoading] = useState(false);
    const [downloadingXlsx, setDownloadingXlsx] = useState(false);
    const [downloadingPdf, setDownloadingPdf] = useState(false);

    useEffect(() => {
        if (Number.isNaN(year) || Number.isNaN(month)) return;
        let cancelled = false;
        const load = async () => {
            setLoading(true);
            try {
                const data = await StatutoryRegisterService.getWageSheet(
                    institutionId, year, month, format, orgUnitId ?? undefined,
                );
                if (!cancelled) setDto(data);
            } catch (e) {
                if (!cancelled) {
                    setDto(null);
                    toast.showApiError(e, 'Failed to load wage sheet');
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        load();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [institutionId, year, month, format, orgUnitId]);

    const handleDownloadXlsx = async () => {
        if (!dto) return;
        setDownloadingXlsx(true);
        try {
            const blob = await StatutoryRegisterService.downloadWageSheetXlsx(
                institutionId, dto.header.year, dto.header.month, dto.header.format, orgUnitId ?? undefined,
            );
            const formatSegment = sanitizeFilenameSegment(dto.header.format, 'FULL').toLowerCase();
            const codeSegment = sanitizeFilenameSegment(dto.header.institutionCode, 'institution');
            const monthSegment = String(dto.header.month).padStart(2, '0');
            triggerDownload(blob, `wage-sheet-${formatSegment}-${codeSegment}-${dto.header.year}-${monthSegment}.xlsx`);
        } catch (e) {
            toast.showApiError(e, 'Download failed');
        } finally {
            setDownloadingXlsx(false);
        }
    };

    const handleDownloadPdf = async () => {
        if (!dto) return;
        setDownloadingPdf(true);
        try {
            const blob = await StatutoryRegisterService.downloadWageSheetPdf(
                institutionId, dto.header.year, dto.header.month, dto.header.format, orgUnitId ?? undefined,
            );
            const formatSegment = sanitizeFilenameSegment(dto.header.format, 'FULL').toLowerCase();
            const codeSegment = sanitizeFilenameSegment(dto.header.institutionCode, 'institution');
            const monthSegment = String(dto.header.month).padStart(2, '0');
            triggerDownload(blob, `wage-sheet-${formatSegment}-${codeSegment}-${dto.header.year}-${monthSegment}.pdf`);
        } catch (e) {
            toast.showApiError(e, 'Download failed');
        } finally {
            setDownloadingPdf(false);
        }
    };

    const renderCell = (row: IWageSheetRow, columnName: string): React.ReactNode => {
        const meta = WAGE_SHEET_COLUMN_META[columnName as keyof typeof WAGE_SHEET_COLUMN_META];
        if (!meta) return dash(null);
        const raw = (row as unknown as Record<string, string | number | boolean | null>)[meta.key];
        switch (meta.kind) {
            case 'MONEY':
            case 'HOURS':
                return money(raw as number | null);
            case 'INT':
                return raw === null || raw === undefined ? '—' : String(raw);
            case 'FLAG':
                return raw ? 'Yes' : '—';
            default:
                return dash(raw as string | null);
        }
    };

    const renderTotalCell = (columnName: string): React.ReactNode => {
        if (!dto) return '—';
        const meta = WAGE_SHEET_COLUMN_META[columnName as keyof typeof WAGE_SHEET_COLUMN_META];
        if (!meta) return '—';
        const raw = (dto.totals as unknown as Record<string, number | null>)[meta.key];
        if (raw === undefined) return '—';
        if (meta.kind === 'MONEY' || meta.kind === 'HOURS') return money(raw);
        if (meta.kind === 'INT') return raw === null ? '—' : String(raw);
        return '—';
    };

    return (
        <Box data-help-page="statutory.wageSheet.page">
            <Box data-help-section="statutory.wageSheet.filter" display="flex" gap={2} alignItems="flex-end" mb={2} flexWrap="wrap">
                <TextField
                    size="small"
                    label="Year"
                    type="number"
                    data-help="statutory.wageSheet.year"
                    value={Number.isNaN(year) ? '' : year}
                    onChange={e => {
                        const v = parseInt(e.target.value, 10);
                        setYear(Number.isNaN(v) ? NaN : v);
                    }}
                    sx={{ flex: '1 1 120px', maxWidth: 192 }}
                />
                <TextField
                    select size="small" label="Month" value={month} data-help="statutory.wageSheet.month"
                    onChange={e => setMonth(parseInt(e.target.value as string, 10))}
                    sx={{ flex: '1 1 150px', maxWidth: 240 }}
                >
                    {MONTH_NAMES.map(m => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
                </TextField>
                <TextField
                    select size="small" label="Format" value={format} data-help="statutory.wageSheet.format"
                    onChange={e => setFormat(e.target.value as WageSheetFormat)}
                    sx={{ flex: '1 1 200px', maxWidth: 320 }}
                >
                    {WAGE_SHEET_FORMATS.map(f => <MenuItem key={f.value} value={f.value}>{f.label}</MenuItem>)}
                </TextField>
                <OrgUnitPicker
                    institutionId={institutionId}
                    value={orgUnitId}
                    onChange={option => setOrgUnitId(option?.id ?? null)}
                    label="Org Unit"
                    placeholder="Filter by department, floor, section or line..."
                    sx={{ flex: '1 1 260px', maxWidth: 400 }}
                    data-testid="wage-sheet-org-unit-filter"
                    data-help="statutory.wageSheet.orgUnit"
                />
                <Button data-help="statutory.wageSheet.downloadXlsx"
                    variant="contained" size="small" startIcon={<FileDownloadIcon />}
                    onClick={handleDownloadXlsx} disabled={!dto || downloadingXlsx}
                    data-testid="wage-sheet-download-xlsx"
                >
                    Download XLSX
                </Button>
                <Button data-help="statutory.wageSheet.downloadPdf"
                    variant="outlined" size="small" startIcon={<FileDownloadIcon />}
                    onClick={handleDownloadPdf} disabled={!dto || downloadingPdf}
                    data-testid="wage-sheet-download-pdf"
                >
                    Download PDF
                </Button>
            </Box>

            {loading && <CircularProgress size={24} />}

            {!loading && dto && (
                <>
                    {dto.caveats.map((c, i) => (
                        <Alert key={i} severity="info" sx={{ mb: 1 }} data-help="statutory.wageSheet.caveat">{c}</Alert>
                    ))}

                    <Typography variant="body2" color="text.secondary" mb={1}>
                        {dto.header.institutionName} -- {dto.header.formatDisplayName} -- {dto.header.rowCount} employee(s)
                    </Typography>

                    <TableContainer component={Paper} data-testid="wage-sheet-table" data-help-section="statutory.wageSheet.table">
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    {dto.columns.map(c => (
                                        <TableCell key={c} {...helpElement(COLUMN_HELP[c]?.helpKey)} align={WAGE_SHEET_COLUMN_META[c]?.kind === 'TEXT' || WAGE_SHEET_COLUMN_META[c]?.kind === 'DATE' ? 'left' : 'right'}>
                                            {WAGE_SHEET_COLUMN_META[c]?.label ?? c}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {dto.rows.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={dto.columns.length} align="center">
                                            <Typography color="text.secondary">No rows for this period.</Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                                {dto.rows.map(row => (
                                    <TableRow key={row.userId} hover>
                                        {dto.columns.map(c => (
                                            <TableCell key={c} align={WAGE_SHEET_COLUMN_META[c]?.kind === 'TEXT' || WAGE_SHEET_COLUMN_META[c]?.kind === 'DATE' ? 'left' : 'right'}>
                                                {renderCell(row, c)}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))}
                            </TableBody>
                            <TableFooter>
                                <TableRow data-help="statutory.wageSheet.totals" sx={{ '& td': { fontWeight: 'bold', borderTop: '2px solid', borderColor: 'divider' } }} data-testid="wage-sheet-totals-row">
                                    {dto.columns.map((c, idx) => (
                                        <TableCell key={c} align={WAGE_SHEET_COLUMN_META[c]?.kind === 'TEXT' || WAGE_SHEET_COLUMN_META[c]?.kind === 'DATE' ? 'left' : 'right'}>
                                            {idx === 0 ? 'Total' : renderTotalCell(c)}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableFooter>
                        </Table>
                    </TableContainer>
                </>
            )}
        </Box>
    );
};

export default WageSheetTab;
